import React, { useState, useEffect } from 'react';
import { X, SlidersHorizontal, Check } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from '../ui/sheet';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';

/**
 * Mobile-optimized filter bottom sheet
 * Slides up from bottom with all filtering options
 */
export const FilterBottomSheet = ({ onApplyFilters, activeFilters = {} }) => {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({
    priceRange: [0, 3000],
    ageGroups: [],
    features: [],
    categories: []
  });

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Fetch categories
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
        const data = await response.json();
        setCategories(data || []);
      } catch (error) {
        console.error('Failed to fetch categories');
      }
    };
    fetchCategories();

    // Initialize from active filters
    if (Object.keys(activeFilters).length > 0) {
      setFilters(activeFilters);
    }
  }, [activeFilters]);

  const ageOptions = [
    { id: '0-2', label: '0-2 years' },
    { id: '3-5', label: '3-5 years' },
    { id: '6-12', label: '6-12 years' },
    { id: '12+', label: '12+ years' }
  ];

  const featureOptions = [
    { id: 'bpa_free', label: 'BPA-Free' },
    { id: 'eco_friendly', label: 'Eco-Friendly' },
    { id: 'washable', label: 'Washable' },
    { id: 'educational', label: 'Educational' },
    { id: 'durable', label: 'Durable' }
  ];

  const handleAgeToggle = (ageId) => {
    setFilters(prev => ({
      ...prev,
      ageGroups: prev.ageGroups.includes(ageId)
        ? prev.ageGroups.filter(id => id !== ageId)
        : [...prev.ageGroups, ageId]
    }));
  };

  const handleFeatureToggle = (featureId) => {
    setFilters(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(id => id !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
    setOpen(false);
  };

  const handleClear = () => {
    const clearedFilters = {
      priceRange: [0, 3000],
      ageGroups: [],
      features: [],
      categories: []
    };
    setFilters(clearedFilters);
    onApplyFilters(clearedFilters);
  };

  const activeFilterCount = 
    filters.ageGroups.length + 
    filters.features.length + 
    filters.categories.length +
    (filters.priceRange[0] > 0 || filters.priceRange[1] < 3000 ? 1 : 0);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 relative"
          data-testid="filter-button"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <Badge className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-cyan-600">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Filter Products</SheetTitle>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Price Range */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Price Range</Label>
            <div className="px-2">
              <Slider
                value={filters.priceRange}
                onValueChange={(value) => setFilters(prev => ({ ...prev, priceRange: value }))}
                max={3000}
                min={0}
                step={100}
                className="mb-3"
              />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>₹{filters.priceRange[0]}</span>
                <span>₹{filters.priceRange[1]}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Age Groups */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Age Group</Label>
            <div className="grid grid-cols-2 gap-2">
              {ageOptions.map((age) => (
                <Button
                  key={age.id}
                  variant={filters.ageGroups.includes(age.id) ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleAgeToggle(age.id)}
                  className="justify-start"
                  data-testid={`filter-age-${age.id}`}
                >
                  {filters.ageGroups.includes(age.id) && <Check className="w-4 h-4 mr-2" />}
                  {age.label}
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          {/* Categories */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Categories</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {categories.slice(0, 8).map((category) => (
                <div key={category.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`category-${category.id}`}
                    checked={filters.categories.includes(category.id)}
                    onCheckedChange={() => handleCategoryToggle(category.id)}
                  />
                  <Label
                    htmlFor={`category-${category.id}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {category.name}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Features */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Features</Label>
            <div className="space-y-2">
              {featureOptions.map((feature) => (
                <div key={feature.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`feature-${feature.id}`}
                    checked={filters.features.includes(feature.id)}
                    onCheckedChange={() => handleFeatureToggle(feature.id)}
                  />
                  <Label
                    htmlFor={`feature-${feature.id}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {feature.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <SheetFooter className="sticky bottom-0 bg-white border-t pt-4 pb-2 flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleClear}
            className="flex-1"
            data-testid="filter-clear"
          >
            Clear All
          </Button>
          <Button
            onClick={handleApply}
            className="flex-1 bg-cyan-600 hover:bg-cyan-700"
            data-testid="filter-apply"
          >
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
